import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Upload, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { FormField } from '@/components/ui/FormField';
import { useUiStore } from '@/stores/uiStore';
import { useProjectTypes } from '@/features/regions/useRegions';
import { api, apiError } from '@/lib/api-client';

/** The "free text allowed" project value that toggles the note field. */
const PROJECT_OTHER = 'Alte';
/** Default project for new uploads. */
const PROJECT_DEFAULT = 'PNERP';

/** Server extracts EXIF (GPS + DateTimeOriginal) from the uploaded bytes,
 *  so the client doesn't need exifr. If the photo has no GPS at all the
 *  server returns a 400 and we surface the message. */

type Props = { open: boolean; onClose: () => void };

export function PhotoUploadDialog({ open, onClose }: Props) {
  const { t } = useTranslation(['photos', 'common']);
  const qc = useQueryClient();
  const pushToast = useUiStore((s) => s.pushToast);

  const projectTypesQ = useProjectTypes();
  const projectOptions = projectTypesQ.data?.items.map((p) => p.name) ?? [];

  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [project, setProject] = useState<string>(PROJECT_DEFAULT);
  const [projectNote, setProjectNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const resetState = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    setProject(PROJECT_DEFAULT);
    setProjectNote('');
    setError(null);
    setBusy(false);
  };

  const handleClose = () => {
    if (busy) return;
    resetState();
    onClose();
  };

  const handleFilePick = (picked: File) => {
    setError(null);
    setFile(picked);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(picked));
  };

  const handleSubmit = async () => {
    if (!file) {
      setError(t('photos:upload.noFile', { defaultValue: 'Selectează o fotografie' }));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('project', project);
      if (project === PROJECT_OTHER && projectNote.trim()) {
        form.append('projectNote', projectNote.trim());
      }

      await api.post('photos/upload', { body: form });
      await qc.invalidateQueries({ queryKey: ['photos'] });
      pushToast({
        message: t('photos:upload.success', { defaultValue: 'Fotografie încărcată.' }),
        variant: 'success',
      });
      handleClose();
    } catch (err) {
      const e = await apiError(err);
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t('photos:upload.title', { defaultValue: 'Încarcă fotografie' })}
    >
      <div className="space-y-4">
        <p className="text-xs text-slate-500">
          {t('photos:upload.hint', {
            defaultValue:
              'Selectează o fotografie JPEG/PNG. Coordonatele GPS și data sunt extrase automat din EXIF.',
          })}
        </p>

        {!file ? (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex w-full flex-col items-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 p-8 text-slate-500 transition hover:border-forest-500 hover:bg-forest-50/30"
          >
            <Upload className="h-8 w-8" />
            <span className="text-sm font-medium">
              {t('photos:upload.pick', { defaultValue: 'Alege fișier...' })}
            </span>
            <span className="text-xs">JPEG, PNG — max 10 MB</span>
          </button>
        ) : (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="flex gap-3">
              <div className="h-24 w-24 shrink-0 overflow-hidden rounded bg-slate-200">
                {previewUrl ? (
                  <img src={previewUrl} alt="preview" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <ImageIcon className="h-8 w-8 text-slate-400" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1 space-y-1 text-xs">
                <p className="truncate font-medium text-slate-800">{file.name}</p>
                <p className="text-slate-500">{(file.size / 1024).toFixed(0)} KB</p>
                <p className="text-slate-500">
                  {t('photos:upload.autoExtract', {
                    defaultValue: 'GPS și data sunt citite automat din EXIF la upload.',
                  })}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (previewUrl) URL.revokeObjectURL(previewUrl);
                  setFile(null);
                  setPreviewUrl(null);
                }}
                className="h-7 rounded px-2 text-xs text-slate-500 hover:bg-slate-200"
              >
                {t('common:common.change', { defaultValue: 'Schimbă' })}
              </button>
            </div>
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFilePick(f);
            e.target.value = '';
          }}
        />

        <FormField label={t('photos:project', { defaultValue: 'Proiect' })} htmlFor="upload-project">
          <Select
            id="upload-project"
            value={project}
            onChange={(e) => setProject(e.target.value)}
          >
            {projectOptions.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </Select>
        </FormField>

        {project === PROJECT_OTHER ? (
          <FormField
            label={t('photos:projectNote', { defaultValue: 'Notă proiect' })}
            htmlFor="upload-projectNote"
          >
            <input
              id="upload-projectNote"
              type="text"
              maxLength={500}
              value={projectNote}
              onChange={(e) => setProjectNote(e.target.value)}
              className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
            />
          </FormField>
        ) : null}

        {error ? (
          <div className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">{error}</div>
        ) : null}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={handleClose} disabled={busy}>
            {t('common:common.cancel', { defaultValue: 'Anulează' })}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!file || busy}
          >
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            {t('photos:upload.submit', { defaultValue: 'Încarcă' })}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
